/**
 * الشات الداخلي.
 *
 * حديث الفريق داخل النظام: قنواتٌ للموضوعات ورسائل مباشرة بين اثنين. وهو
 * غير البريد الوارد — ذاك يصل من خارج الشركة وله عنوان وموضوع وخيط، وهذا
 * كلامٌ بين من يعملون معًا.
 *
 * ## بالسحب لا بالدفع
 *
 * لا مقبس ويب. الدفع الفوري يحتاج اتصالًا حيًّا لكل مستخدم وحالةً في ذاكرة
 * العملية، ونسختان من الخادم تعنيان أن رسالة كُتبت على إحداهما لا تصل إلى
 * من يستمع على الأخرى — وهو عطبٌ صامت لا يظهر إلا في الإنتاج.
 *
 * والسحب المتقارب يعمل في كل نشر: المحادثة المفتوحة تُسأل كل بضع ثوانٍ،
 * والعدّاد كل ربع دقيقة، وكلاهما يتوقّف حين يغيب التبويب. التأخير ثوانٍ
 * معدودة، وهو ثمنٌ مقبول مقابل ألّا تضيع رسالة.
 *
 * ## وغير المقروء بعلامةٍ واحدة لكل عضو
 *
 * `lastReadAt` على العضوية لا صفَّ قراءةٍ لكل رسالة لكل مستخدم. عشرة أشخاص
 * وألف رسالة يعني عشرة آلاف صفّ لا تُقرأ إلا مجموعةً — والعدّ من علامةٍ
 * واحدة استعلامٌ واحد.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   الأنواع
   ========================================================================== */

export const CONVERSATION_KINDS = ['CHANNEL', 'DIRECT'] as const;
export type ConversationKind = (typeof CONVERSATION_KINDS)[number];

export interface ConversationKindDef extends Labelled<ConversationKind> {
  description: string;
}

export const CONVERSATION_KIND_DEFS: readonly ConversationKindDef[] = [
  {
    key: 'CHANNEL',
    label: 'قناة',
    kind: 'info',
    description: 'موضوعٌ يتابعه من يعنيه: فريق، أو مشروع، أو إعلانات.',
  },
  {
    key: 'DIRECT',
    label: 'رسالة مباشرة',
    kind: 'neutral',
    description: 'بينك وبين زميل، لا يقرؤها غيركما.',
  },
] as const;

export const CONVERSATION_KIND_MAP = Object.fromEntries(
  CONVERSATION_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ConversationKind, ConversationKindDef>>;

/* ==========================================================================
   الحدود
   ========================================================================== */

export const MAX_MESSAGE_CHARS = 4000;

/** مهلة تعديل الرسالة بالدقائق — كمهلة الملاحظة، وللسبب نفسه. */
export const CHAT_EDIT_MINUTES = 15;

/** كم رسالة تُجلب في الصفحة الواحدة. */
export const CHAT_PAGE_SIZE = 50;

/** أقصى عدد أعضاء في قناة. */
export const MAX_CHANNEL_MEMBERS = 200;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface ChatMessageView {
  id: string;
  conversationId: string;
  authorId: string | null;
  /** اسم الكاتب وقت الكتابة — يبقى بعد مغادرته الشركة. */
  authorName: string | null;
  body: string;
  mentions: string[];
  createdAt: string;
  editedAt: string | null;
  /** حُذفت — يبقى مكانها معلَّمًا فلا ينقطع سياق ما بعدها. */
  deletedAt: string | null;
  /** حدثٌ في المجموعة — يُعرض سطرًا في المنتصف، ولا يُعدَّل ولا يُحذف. */
  system: boolean;
}

export interface ConversationView {
  id: string;
  kind: ConversationKind;
  /** اسم القناة، أو اسم الطرف الآخر في المباشرة. */
  title: string;
  topic: string | null;
  isPrivate: boolean;
  /** الطرف الآخر في المباشرة — لصورته في القائمة. */
  peerId: string | null;
  memberCount: number;
  /** آخر ما قيل، مقتطعًا — القائمة تُقرأ بلا فتح كل محادثة. */
  lastBody: string | null;
  lastAt: string | null;
  lastAuthorName: string | null;
  unread: number;
  /** هل أنا عضو — القناة العامة تُقرأ قبل الانضمام. */
  joined: boolean;
  /** منشئها — يُخرج الأعضاء من مجموعته كما يُخرجهم مديرُ الحساب. */
  createdBy: string | null;
}

/* ==========================================================================
   القواعد
   ========================================================================== */

/**
 * هل ما زال تعديل الرسالة متاحًا.
 *
 * دالة خالصة يستعملها الخادم والواجهة: الواجهة تُخفي الزرّ، والخادم يرفض
 * الطلب. وإخفاءٌ بلا رفض ليس قاعدة.
 */
export function canEditMessage(createdAt: string | Date, now: Date = new Date()): boolean {
  const at = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  return now.getTime() - at.getTime() <= CHAT_EDIT_MINUTES * 60_000;
}

/**
 * مفتاح المحادثة المباشرة.
 *
 * معرّفا الطرفين مرتَّبين. بدونه يفتح كلٌّ منهما محادثةً مع الآخر فيصير
 * بينهما خيطان، ويكتب هذا في واحد ويقرأ ذاك من الآخر — فيظنّ كلٌّ أن
 * صاحبه لا يردّ.
 *
 * والترتيب هو ما يجعله واحدًا مهما بدأ.
 */
export function directKey(a: string, b: string): string {
  return [a, b].sort().join(':');
}

/**
 * مقتطف الرسالة لقائمة المحادثات.
 *
 * سطرٌ واحد بلا أسطر جديدة: رسالةٌ من عشرة أسطر تدفع بقية المحادثات خارج
 * الشاشة.
 */
export function snippet(body: string, max = 80): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

/**
 * اسمُ نوع المحادثة كما يقرؤه الموظّف.
 *
 * القناةُ الخاصّة هي «المجموعة»: أعضاءٌ مختارون يتحدّثون ولا يراهم غيرُهم.
 * لا نوعَ ثالثًا في القاعدة — مجموعةٌ وقناةٌ خاصّة شيءٌ واحد بقاعدةِ رؤيةٍ
 * واحدة، وفصلُهما نوعين يعني قاعدتين تنحرف إحداهما يومًا عن الأخرى.
 */
export function conversationLabel(c: Pick<ConversationView, 'kind' | 'isPrivate'>): string {
  if (c.kind === 'DIRECT') return 'محادثة';
  return c.isPrivate ? 'مجموعة' : 'قناة';
}

/**
 * نصوصُ أحداث المجموعة.
 *
 * مبنيّةٌ للمجهول: «أُضيف عمر» تصحّ لعمر ولسارة، و«أضاف» تُخطئ في نصف
 * الحالات. والفاعلُ يُكتب اسمُه في سطرٍ صغيرٍ تحتها من `authorName`.
 */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join('، ')}، و${names[names.length - 1]}`;
}

export const CHAT_EVENTS = {
  created: (name: string) => `أُنشئت «${name}»`,
  added: (names: readonly string[]) => `أُضيف: ${joinNames(names)}`,
  removed: (names: readonly string[]) => `أُخرج: ${joinNames(names)}`,
  left: (name: string) => `خروجٌ من المحادثة: ${name}`,
  renamed: (name: string) => `تغيّر الاسم إلى «${name}»`,
} as const;
